import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getBookingConfirmation, type BookingConfirmation } from "@/lib/booking-confirmation.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/bartender/booking-confirmation")({
  head: () => ({ meta: [{ title: "Booking Confirmed — The Hospitality Reserve" }] }),
  validateSearch: (search: Record<string, unknown>): { shift_id?: string } => ({
    shift_id: typeof search.shift_id === "string" ? search.shift_id : undefined,
  }),
  component: BookingConfirmationPage,
});

function gbp(pounds: number): string {
  return `£${pounds.toFixed(2)}`;
}

function formatDateRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const sameDay = start.toDateString() === end.toDateString();
  const date = start.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const startTime = start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const endTime = end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `${date} · ${startTime} – ${endTime}`;
  return `${start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} ${startTime} – ${end.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })} ${endTime}`;
}

function BookingConfirmationPage() {
  const { shift_id } = Route.useSearch();
  const fetch = useServerFn(getBookingConfirmation);
  const [data, setData] = useState<BookingConfirmation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shift_id) {
      setError("No shift specified.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch({ data: { shift_id } });
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load confirmation.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shift_id, fetch]);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-3xl px-6 py-12">
        <Link
          to="/bartender"
          className="inline-block text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to shift feed
        </Link>

        {loading && (
          <div className="mt-10 border border-border bg-card p-10 text-center text-sm text-muted-foreground">
            Loading your confirmation…
          </div>
        )}

        {!loading && error && (
          <div className="mt-10 border border-destructive/40 bg-destructive/5 p-10 text-center">
            <p className="text-sm text-destructive">{error}</p>
            <Button asChild className="mt-6 rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury">
              <Link to="/bartender">Return to shift feed</Link>
            </Button>
          </div>
        )}

        {!loading && data && (
          <div className="mt-8 space-y-6">
            <div className="border border-emerald-600/40 bg-emerald-600/5 p-6 text-center">
              <p className="tracking-wide-luxury text-emerald-600">Confirmed</p>
              <h1 className="mt-3 font-display text-3xl">You're booked</h1>
              <span className="gold-divider mx-auto mt-4 block w-12" />
              <p className="mt-4 text-sm text-foreground/80">
                {data.filled
                  ? "This engagement is now filled. The venue will complete payment to confirm the booking."
                  : "Your application was accepted. The engagement will be confirmed once the venue fills all positions."}
              </p>
            </div>

            <div className="border border-border bg-card p-6">
              <p className="tracking-wide-luxury text-accent">Engagement details</p>
              <h2 className="mt-3 font-display text-2xl">
                {data.venue.full_name || "Venue"}
              </h2>
              {data.venue.headline && (
                <p className="mt-1 text-sm text-muted-foreground">{data.venue.headline}</p>
              )}

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-[0.65rem] tracking-wide-luxury text-muted-foreground">When</p>
                  <p className="mt-1 text-sm text-foreground/90">{formatDateRange(data.shift.start_time, data.shift.end_time)}</p>
                </div>
                <div>
                  <p className="text-[0.65rem] tracking-wide-luxury text-muted-foreground">Role</p>
                  <p className="mt-1 text-sm text-foreground/90 capitalize">{data.shift.role_type.replace(/_/g, " ")}</p>
                </div>
                <div>
                  <p className="text-[0.65rem] tracking-wide-luxury text-muted-foreground">Location</p>
                  <p className="mt-1 text-sm text-foreground/90">
                    {[data.shift.address, data.shift.postcode].filter(Boolean).join(" · ") || "Contact venue for address"}
                  </p>
                </div>
                <div>
                  <p className="text-[0.65rem] tracking-wide-luxury text-muted-foreground">Status</p>
                  <p className="mt-1 text-sm text-foreground/90">
                    {data.paid
                      ? "Paid & confirmed"
                      : data.filled
                        ? "Filled — awaiting venue payment"
                        : "Accepted — filling remaining positions"}
                  </p>
                </div>
              </div>

              {data.shift.requires_dbs && (
                <p className="mt-4 text-xs text-amber-600">
                  This shift requires an Enhanced DBS on file.
                </p>
              )}

              {data.shift.notes && (
                <div className="mt-4 border-t border-border pt-4">
                  <p className="text-[0.65rem] tracking-wide-luxury text-muted-foreground">Venue notes</p>
                  <p className="mt-1 text-sm text-foreground/80">{data.shift.notes}</p>
                </div>
              )}
            </div>

            <div className="border border-border bg-card p-6">
              <p className="tracking-wide-luxury text-accent">Your earnings</p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-4xl">{gbp(data.earnings.total)}</span>
                <span className="text-sm text-muted-foreground">
                  {gbp(data.earnings.hourly_rate)} / hr · {data.earnings.hours.toFixed(1)} hrs
                </span>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                You keep 100% of your wage. Any applicable late-arrival deductions will be calculated after the shift.
              </p>
            </div>

            <div className="border border-border bg-card p-6">
              <p className="tracking-wide-luxury text-accent">What's next</p>
              <ol className="mt-4 space-y-3 text-sm text-foreground/80">
                <li className="flex items-start gap-3">
                  <span className="text-xs tracking-wide-luxury text-emerald-600">01</span>
                  <span>The venue completes payment to lock in the booking.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-xs tracking-wide-luxury text-muted-foreground">02</span>
                  <span>Arrive on time and confirm your arrival from the Active Engagements screen.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-xs tracking-wide-luxury text-muted-foreground">03</span>
                  <span>After the shift, both sides confirm completion and your payout is queued.</span>
                </li>
              </ol>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button asChild className="rounded-none bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury">
                <Link to="/shift/$id/notes" params={{ id: data.shift.id }}>
                  Message venue
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-none border-border tracking-wide-luxury text-xs">
                <Link to="/bartender">View all engagements</Link>
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
